import React from "react";
import { X, FileText, Download, Mail, Phone, MapPin, Calendar, ShieldCheck, Car, Globe, Send, FileSignature, Home, Hash, Loader2, CheckCircle2, Eye, UserX } from "lucide-react";
import { adminApi, API } from "../lib/api";

const DEFAULT_REJECTION_REASON =
  "Vi har desværre valgt at gå videre med andre kandidater og kan derfor ikke tilbyde dig en stilling på nuværende tidspunkt.";

const KIND_LABEL = {
  drivers_license: "Kørekort",
  drivers_license_front: "Kørekort (forside)",
  drivers_license_back: "Kørekort (bagside)",
  criminal_record: "Straffeattest",
  work_permit: "Opholdstilladelse / Arbejdstilladelse",
  passport: "Pas",
  cv: "CV",
};

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon size={16} className="text-brand-text2 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-xs uppercase tracking-wider text-brand-text2">{label}</div>
      <div className="text-sm text-brand-dark break-words">{children || <span className="text-brand-text2 italic">Ikke angivet</span>}</div>
    </div>
  </div>
);

export default function DriverApplicationDetailModal({ appId, onClose }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [signing, setSigning] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [previewing, setPreviewing] = React.useState(false);
  const [emailingBurd, setEmailingBurd] = React.useState(false);
  const [resendingContract, setResendingContract] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setErr(null);
    try {
      const { data } = await adminApi().get(`/admin/driver-applications/${appId}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Kunne ikke hente ansøgning");
    } finally {
      setLoading(false);
    }
  }, [appId]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!appId || !alive) return;
      await reload();
    })();
    return () => { alive = false; };
  }, [appId, reload]);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const downloadDoc = async (kind, filename) => {
    try {
      const token = localStorage.getItem("ema_admin_token");
      const url = `${API}/admin/driver-applications/${appId}/documents/${kind}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
      if (!res.ok) {
        let detail = res.status;
        try { detail = (await res.json()).detail || detail; } catch { /* not json */ }
        alert(`Kunne ikke hente dokument: ${detail}`);
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || `${kind}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      alert(`Fejl: ${e.message}`);
    }
  };

  const sendForSigning = async () => {
    if (isSigned) {
      if (!window.confirm(`⚠️  Kontrakten er ALLEREDE underskrevet af ${signingStatus?.typed_name || data?.full_name}.\n\nHvis du sender et nyt link, NULSTILLER det den eksisterende underskrift, og ansøgeren skal underskrive igen.\n\nVil du fortsætte?`)) return;
    } else if (!window.confirm(`Send underskriv-link til ${data?.email}?\n\nAnsøgeren modtager en email med et magic link og kan underskrive ansættelseskontrakten digitalt.`)) {
      return;
    }
    setSigning(true);
    try {
      const { data: res } = await adminApi().post(`/admin/driver-applications/${appId}/contract/send-for-signing`);
      if (res?.ok) {
        alert(`✓ Underskriv-link sendt til ${data.email}\n\nLink (gyldigt 14 dage):\n${res.sign_link}`);
        await reload();
      }
    } catch (e) {
      alert(`Fejl: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSigning(false);
    }
  };

  const fetchBurdBlob = async () => {
    const token = localStorage.getItem("ema_admin_token");
    const url = `${API}/admin/driver-applications/${appId}/burd-pdf`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
    if (!res.ok) {
      let detail = `Fejl ${res.status}`;
      try { detail = (await res.json()).detail || detail; } catch { /* not json */ }
      throw new Error(detail);
    }
    return res.blob();
  };

  const emailBurdPdf = async () => {
    setEmailingBurd(true);
    try {
      const { data } = await adminApi().post(`/admin/driver-applications/${appId}/email-burd-packet`);
      if (data?.ok) {
        alert(`✓ Burd-pakken er sendt som vedhæftning til ${data.sent_to}\n\nStørrelse: ${Math.round((data.size_bytes || 0) / 1024)} KB`);
      } else {
        alert("Mailen kunne ikke sendes — tjek SMTP-konfiguration");
      }
    } catch (e) {
      alert(`Fejl: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setEmailingBurd(false);
    }
  };

  const resendContractCopy = async () => {
    const defaultEmail = data?.email || "";
    const target = window.prompt(
      `Send underskrevet ansættelseskontrakt til medarbejderen.\n\nModtager-email:`,
      defaultEmail,
    );
    if (!target || !target.includes("@")) {
      if (target !== null) alert("Ugyldig email-adresse");
      return;
    }
    setResendingContract(true);
    try {
      const { data: r } = await adminApi().post(
        `/admin/driver-applications/${appId}/resend-contract-copy`,
        { to_email: target },
      );
      if (r?.ok) {
        alert(`✓ Kontrakten er sendt som vedhæftning til ${r.sent_to}`);
      } else {
        alert("Mailen kunne ikke sendes — tjek SMTP-konfiguration");
      }
    } catch (e) {
      alert(`Fejl: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setResendingContract(false);
    }
  };

  const previewBurdPdf = async () => {
    setPreviewing(true);
    try {
      const blob = await fetchBurdBlob();
      const objectUrl = URL.createObjectURL(blob);
      const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!win) {
        alert("Browseren blokerede pop-up. Tillad pop-ups for emakurer.dk eller brug Hent-knappen.");
      }
      // Revoke after 2 minutes so the new tab has time to render the PDF
      setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
    } catch (e) {
      alert(`Fejl: ${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const downloadBurdPdf = async () => {
    setDownloading(true);
    try {
      const blob = await fetchBurdBlob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `burd-pakke-${(data?.full_name || appId).replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      alert(`Fejl: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const signingStatus = data?.signing;
  const isSigned = signingStatus?.status === "signed";
  const signSent = signingStatus?.status === "sent" || isSigned;

  // Compute missing required documents
  const requiredKinds = ["drivers_license_front", "drivers_license_back", "criminal_record"];
  if (data?.is_danish_citizen) requiredKinds.push("passport"); else if (data) requiredKinds.push("work_permit");
  const haveKinds = new Set((data?.documents || []).map((d) => d?.kind).filter(Boolean));
  const missingKinds = requiredKinds.filter((k) => !haveKinds.has(k));
  const missingLabels = {
    drivers_license_front: "Kørekort (forside)",
    drivers_license_back: "Kørekort (bagside)",
    criminal_record: "Straffeattest",
    passport: "Pas",
    work_permit: "Opholdstilladelse",
  };
  const [requestingDocs, setRequestingDocs] = React.useState(false);

  const requestMissingDocs = async () => {
    if (!window.confirm(`Send email til ${data?.email} med upload-link for de manglende dokumenter:\n\n${missingKinds.map((k) => "• " + (missingLabels[k] || k)).join("\n")}`)) return;
    setRequestingDocs(true);
    try {
      const { data: res } = await adminApi().post(`/admin/driver-applications/${appId}/request-missing-docs`);
      if (res?.ok) {
        alert(`✓ Email sendt til ${data.email}.\n\nUpload-link (gyldigt 14 dage):\n${res.upload_link}`);
      }
    } catch (e) {
      alert(`Fejl: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRequestingDocs(false);
    }
  };

  const [sendingOnb, setSendingOnb] = React.useState(false);
  const [rejecting, setRejecting] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState(DEFAULT_REJECTION_REASON);
  const [showRejectForm, setShowRejectForm] = React.useState(false);

  const sendOnboardingLink = async () => {
    if (!window.confirm(`Send onboarding-link til ${data?.email}?`)) return;
    setSendingOnb(true);
    try {
      const { data: res } = await adminApi().post(`/admin/driver-applications/${appId}/send-onboarding-link`);
      if (res?.ok) {
        alert(`✓ Onboarding-link sendt til ${data.email}\n\nLink (gyldigt 14 dage):\n${res.link}`);
        await reload();
      }
    } catch (e) {
      alert(`Fejl: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSendingOnb(false);
    }
  };

  const rejectApplication = async () => {
    const reason = (rejectReason || "").trim() || DEFAULT_REJECTION_REASON;
    if (!window.confirm(
      `Afvis ${data?.full_name || "ansøgeren"}?\n\nDer sendes en mail til ${data?.email} om, at personen ikke er blevet optaget.\n\nFortsæt?`
    )) return;
    setRejecting(true);
    try {
      const { data: res } = await adminApi().post(`/admin/driver-applications/${appId}/reject`, {
        reason,
        send_email: true,
      });
      if (res?.ok) {
        alert(
          res.email_sent
            ? `✓ Ansøgningen er afvist.\nAfvisningsmail sendt til ${data.email}.`
            : `✓ Ansøgningen er afvist.\n(Mailen kunne ikke sendes — tjek SMTP.)`
        );
        setShowRejectForm(false);
        await reload();
      }
    } catch (e) {
      alert(`Fejl: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRejecting(false);
    }
  };

  const track = data?.onboarding_tracking;
  const isRejected = data?.status === "rejected";
  const onbSteps = [
    { label: "Sendt", ok: !!track?.sent_at, at: track?.sent_at },
    { label: "Åbnet mail", ok: !!track?.email_opened_at, at: track?.email_opened_at, note: "Ikke sporbar / ikke åbnet" },
    { label: "Klikket link", ok: !!track?.link_clicked_at, at: track?.link_clicked_at },
    { label: "Udfyldt", ok: !!track?.completed, at: track?.completed_at },
  ];

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="driver-app-modal-backdrop"
    >
      <div
        className="bg-white border border-brand max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="driver-app-modal"
      >
        <div className="sticky top-0 bg-white border-b border-brand px-6 py-4 flex items-center justify-between">
          <div>
            <div className="kicker">Chauffør-ansøgning</div>
            <h2 className="font-display font-semibold text-xl text-brand-dark">
              {data?.full_name || (loading ? "Indlæser…" : "Ansøgning")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-brand-bg2"
            data-testid="driver-app-modal-close"
            aria-label="Luk"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {loading && <div className="text-sm text-brand-text2">Indlæser ansøgning…</div>}
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">{err}</div>}

          {data && (
            <>
              {/* Onboarding status — primary overview */}
              <div className="mb-6 border border-brand bg-brand-bg2 p-4" data-testid="application-onboarding-tracking">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <div className="kicker">Onboarding-status</div>
                    <div className="font-display font-semibold text-brand-dark mt-1">
                      {!track ? "Ikke sendt endnu"
                        : track.completed ? "Udfyldt ✓"
                        : track.link_clicked_at ? "Har åbnet linket — afventer udfyldning"
                        : track.email_opened_at ? "Har åbnet mailen"
                        : "Sendt — afventer aktivitet"}
                    </div>
                  </div>
                  <button
                    onClick={sendOnboardingLink}
                    disabled={sendingOnb || isRejected}
                    className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
                    data-testid="app-send-onboarding-link"
                    title={isRejected ? "Ansøgningen er afvist" : undefined}
                  >
                    {sendingOnb ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                    {track?.sent_at ? "Send onboarding igen" : "Send onboarding"}
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                  {onbSteps.map((s) => (
                    <div key={s.label} className={`p-2 border ${s.ok ? "bg-emerald-50 border-emerald-200" : "bg-white border-brand"}`}>
                      <div className={`font-bold ${s.ok ? "text-emerald-800" : "text-brand-text2"}`}>
                        {s.ok ? "✓" : "○"} {s.label}
                      </div>
                      <div className="text-brand-text2 mt-0.5">
                        {s.at
                          ? new Date(s.at).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                          : (s.note && track?.sent_at ? s.note : "Afventer")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reject applicant — not hired / other candidates */}
              <div
                className={`mb-6 border p-4 ${isRejected ? "border-slate-300 bg-slate-50" : "border-red-200 bg-red-50/40"}`}
                data-testid="application-reject-panel"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="kicker">Afgørelse</div>
                    <div className="font-display font-semibold text-brand-dark mt-1">
                      {isRejected ? "Ikke optaget — afvist" : "Afvis ansøger"}
                    </div>
                    <p className="text-sm text-brand-text2 mt-1 max-w-xl">
                      {isRejected
                        ? `Afvist ${data.rejected_at ? new Date(data.rejected_at).toLocaleString("da-DK") : ""}${data.rejected_by ? ` · ${data.rejected_by}` : ""}`
                        : "Send besked om, at personen ikke er blevet optaget, fordi I er gået videre med andre kandidater."}
                    </p>
                    {isRejected && data.rejection_reason && (
                      <p className="text-sm text-brand-dark mt-2 italic border-l-2 border-slate-300 pl-3">
                        {data.rejection_reason}
                      </p>
                    )}
                  </div>
                  {!isRejected && (
                    <button
                      type="button"
                      onClick={() => setShowRejectForm((v) => !v)}
                      className="flex items-center gap-2 text-sm px-3 py-2 border border-red-300 text-red-800 bg-white hover:bg-red-50 font-semibold"
                      data-testid="app-reject-toggle"
                    >
                      <UserX size={14} />
                      {showRejectForm ? "Skjul" : "Afvis ansøger"}
                    </button>
                  )}
                </div>
                {!isRejected && showRejectForm && (
                  <div className="mt-4 space-y-3" data-testid="application-reject-form">
                    <label className="block text-xs uppercase tracking-wider text-brand-text2">
                      Besked i mailen
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={3}
                        className="mt-1 w-full border border-brand px-3 py-2 text-sm text-brand-dark bg-white"
                        data-testid="app-reject-reason"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={rejectApplication}
                      disabled={rejecting}
                      className="flex items-center gap-2 text-sm px-4 py-2 bg-red-700 text-white hover:bg-red-800 font-semibold disabled:opacity-60"
                      data-testid="app-reject-confirm"
                    >
                      {rejecting ? <Loader2 className="animate-spin" size={14} /> : <UserX size={14} />}
                      Afvis og send mail
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <Row icon={Mail} label="Email">{data.email}</Row>
                <Row icon={Phone} label="Telefon">{data.phone}</Row>
                <Row icon={Home} label="Adresse">{data.address}</Row>
                <Row icon={MapPin} label="Postnr. / By">
                  {(data.zip_code || data.city) ? `${data.zip_code || ""} ${data.city || ""}`.trim() : null}
                </Row>
                <Row icon={Hash} label="Nummerplade">
                  {data.license_plate ? <span className="font-mono uppercase tracking-wider">{data.license_plate}</span> : null}
                </Row>
                <Row icon={Calendar} label="Modtaget">{data.created_at ? new Date(data.created_at).toLocaleString("da-DK") : null}</Row>
                <Row icon={Car} label="Kørekort / Egen bil">
                  {data.has_drivers_license ? "Kørekort ✓" : "Intet kørekort"} · {data.has_own_vehicle ? "Egen bil ✓" : "Ingen bil"}
                </Row>
                <Row icon={Globe} label="Statsborgerskab">
                  {data.is_danish_citizen === true ? "Dansk statsborger" : data.is_danish_citizen === false ? `Ikke dansk${data.nationality ? ` · ${data.nationality}` : ""}` : "—"}
                </Row>
                <Row icon={ShieldCheck} label="GDPR-samtykke">
                  {data.gdpr_consent
                    ? `Givet ${data.gdpr_consent_at ? new Date(data.gdpr_consent_at).toLocaleString("da-DK") : ""}`
                    : "Ikke registreret"}
                </Row>
                <Row icon={ShieldCheck} label="Status">
                  {isRejected ? (
                    <span className="text-red-800 font-semibold">Afvist / ikke optaget</span>
                  ) : (
                    data.status || "received"
                  )}
                </Row>
              </div>

              {/* Missing documents alert + request button */}
              {missingKinds.length > 0 && (
                <div className="mt-6 border border-amber-300 bg-amber-50 p-4" data-testid="missing-docs-panel">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-amber-900">⚠ Mangler {missingKinds.length} dokument{missingKinds.length > 1 ? "er" : ""}</div>
                      <ul className="mt-1 text-sm text-amber-900 list-disc list-inside">
                        {missingKinds.map((k) => <li key={k}>{missingLabels[k] || k}</li>)}
                      </ul>
                    </div>
                    <button
                      onClick={requestMissingDocs}
                      disabled={requestingDocs}
                      className="bg-amber-700 text-white hover:bg-amber-800 px-3 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
                      data-testid="request-missing-docs-btn"
                      title="Send email til ansøgeren med upload-link"
                    >
                      {requestingDocs ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      Bed ansøger upload
                    </button>
                  </div>
                </div>
              )}

              {/* Digital signing status + Burd actions */}
              <div className="mt-6 border border-brand bg-brand-bg2 p-5" data-testid="signing-panel">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="kicker">Digital underskrift</div>
                    {isSigned ? (
                      <div className="mt-2 flex items-start gap-2 text-emerald-800" data-testid="signing-signed">
                        <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold">Underskrevet</div>
                          <div className="text-xs text-brand-text2 mt-0.5">
                            {signingStatus.signed_at ? new Date(signingStatus.signed_at).toLocaleString("da-DK") : ""} ·
                            som "<span className="italic">{signingStatus.typed_name}</span>" · IP {signingStatus.ip}
                          </div>
                        </div>
                      </div>
                    ) : signSent ? (
                      <div className="mt-2 text-amber-800" data-testid="signing-sent">
                        Link sendt {signingStatus.sent_at ? new Date(signingStatus.sent_at).toLocaleString("da-DK") : ""} — afventer ansøgerens underskrift
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-brand-text2" data-testid="signing-not-sent">
                        Ingen underskriv-link sendt endnu
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={sendForSigning}
                      disabled={signing}
                      className="btn-secondary flex items-center gap-2 text-sm"
                      data-testid="send-sign-link"
                    >
                      {signing ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      {signSent ? "Send link igen" : "Send underskriv-link"}
                    </button>
                    <button
                      onClick={previewBurdPdf}
                      disabled={previewing}
                      className="btn-secondary flex items-center gap-2 text-sm"
                      data-testid="preview-burd-pdf"
                      title="Åbner Burd-pakken i en ny fane uden at downloade"
                    >
                      {previewing ? <Loader2 className="animate-spin" size={14} /> : <Eye size={14} />}
                      Forhåndsvis
                    </button>
                    <button
                      onClick={emailBurdPdf}
                      disabled={emailingBurd}
                      className="btn-secondary flex items-center gap-2 text-sm"
                      data-testid="email-burd-pdf"
                      title="Send Burd-pakken som vedhæftning til din admin-email"
                    >
                      {emailingBurd ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />}
                      Send til min email
                    </button>
                    <button
                      onClick={downloadBurdPdf}
                      disabled={downloading}
                      className="btn-primary flex items-center gap-2 text-sm"
                      data-testid="download-burd-pdf"
                      title="Genererer én samlet PDF til Burd Delivery med chauffør-info, dokumenter og ansættelseskontrakt"
                    >
                      {downloading ? <Loader2 className="animate-spin" size={14} /> : <FileSignature size={14} />}
                      Hent Burd-pakke (PDF)
                    </button>
                    <button
                      onClick={resendContractCopy}
                      disabled={resendingContract}
                      className="btn-secondary flex items-center gap-2 text-sm"
                      data-testid="resend-contract-copy"
                      title="Send en frisk kopi af den underskrevne ansættelseskontrakt til medarbejderen"
                    >
                      {resendingContract ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      Send kontrakt til medarbejder
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="kicker">Motivation</div>
                <div className="mt-1 bg-brand-bg2 border border-brand p-4 text-sm whitespace-pre-wrap text-brand-dark min-h-[60px]" data-testid="driver-app-motivation">
                  {data.motivation || <span className="text-brand-text2 italic">Ikke udfyldt</span>}
                </div>
              </div>

              <div className="mt-5">
                <div className="kicker">Erfaring</div>
                <div className="mt-1 bg-brand-bg2 border border-brand p-4 text-sm whitespace-pre-wrap text-brand-dark min-h-[60px]" data-testid="driver-app-experience">
                  {data.experience || <span className="text-brand-text2 italic">Ikke udfyldt</span>}
                </div>
              </div>

              <div className="mt-6">
                <div className="kicker">Uploadede dokumenter</div>
                {(!data.documents || data.documents.length === 0) ? (
                  <div className="mt-2 text-sm text-brand-text2 italic">Ingen dokumenter vedlagt</div>
                ) : (
                  <ul className="mt-2 divide-y divide-brand border border-brand bg-white" data-testid="driver-app-documents">
                    {data.documents.map((d) => (
                      <li key={d.kind} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText size={16} className="text-brand-text2 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-brand-dark truncate">
                              {KIND_LABEL[d.kind] || d.kind}
                            </div>
                            <div className="text-xs text-brand-text2 truncate">
                              {d.filename || "—"} · {formatBytes(d.size_bytes)}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => downloadDoc(d.kind, d.filename)}
                          className="btn-secondary flex items-center gap-1 text-xs px-3 py-1.5"
                          data-testid={`download-doc-${d.kind}`}
                        >
                          <Download size={12} /> Hent
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
